import React, { useContext, useEffect, useState } from 'react';
import Typography from '@material-ui/core/Typography';
import CheckCircle from '@material-ui/icons/CheckCircleOutline';
import HighlightOff from '@material-ui/icons/HighlightOff';
import RadioButtonUncheckedIcon from '@material-ui/icons/RadioButtonUnchecked';
import RadioButtonCheckedIcon from '@material-ui/icons/RadioButtonChecked';
import PendingIcon from '@material-ui/icons/FiberManualRecord';
import { makeStyles } from "@material-ui/core/styles";
import { MilestoneTypes } from "../../utils/constants";
import Button from "@material-ui/core/Button";
import { canEditInitiative } from "../../utils/initiatives";
import apiFetch, { getData } from "../../utils/apiUtils";
import { ErrorContext } from "../../utils/constants";
import { useParams } from "react-router-dom";
import { loggedInUserInList } from '../../auth/msalConfig';
import Colors from '../../utils/colors';
import '@fontsource/inter';

const textStyle = {
  fontFamily: 'Inter',
  fontStyle: 'normal',
  fontWeight: 500,
};

const useStyles = makeStyles((theme) => ({
  cardRoot: {
    width: '100%',
    flexGrow: 1,
  },
  milestoneCardHeader: {
    ...textStyle,
    fontSize: '14px',
    lineHeight: '16px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase'
  },
  approvalButton: {
    margin: '1rem',
    textAlign: 'center'
  },
  approvalContainer: {
    display: 'flex',
    justifyContent: 'space-around',
    width: '80%'
  },
  mandatoryApproverStyle: {
    backgroundColor: Colors.darkBlue,
    "&:hover": {
      backgroundColor: Colors.brightBlue,
      color: Colors.indigoBlue
    },
    width: '30%'
  },
  statusDiv: {
    display: 'flex',
    justifyContent: 'center',
    width: '100%'
  },

  //radio buttons
  container: {
    flexGrow: 1,
    display: 'grid',
    gridTemplateColumns: 'repeat(12, 1fr)',
  },
  gridItem: {
    textAlign: "center",
  },
  unSelected: {
    height: '32px',
    width: '32px',
    textAlign: 'center',
    //color: '#00A4E4',
    color: Colors.primary,
    cursor: 'pointer',
  },
  unSelectedNoCursor: {
    height: '32px',
    width: '32px',
    textAlign: 'center',
    //color: '#00A4E4',
    color: Colors.primary,
  },
  selected: {
    height: '32px',
    width: '32px',
    textAlign: 'center',
    //color: '#00A4E4',
    color: Colors.primary,
    cursor: 'pointer'
  },
  checkedCircle: {
    height: '32px',
    width: '32px',
    textAlign: 'center',
    //color: Colors.tertiary,
    color: Colors.primary,
  },
  highlightOff: {
    height: '32px',
    width: '32px',
    textAlign: 'center',
    color: '#db6a34',
  },
  pendingIcon: {
    height: '34px',
    width: '34px',
    color: 'rgba(110, 247, 138, 0.55)',
    //stroke: '#00A4E4',
    stroke: 'rgba(0, 15, 86, 0.8)', 
    strokeWidth: '2',
  },

  //Grid Container
  gridContainer: {
    display: 'grid',
    gridTemplateColumns: 'auto auto auto',
    backgroundColor: 'white',

  },
  gridItem: {
    backgroundColor: 'rgba(255, 255, 255, 0.8)',
    padding: '2px',
    fontSize: '15px',
    textAlign: 'center'
  }
}));

const formatType = (type) => {
  return MilestoneTypes[type].label;
};

export default function StakeholderMilestoneCard({ index, initiativeTeam, milestoneReviewers, onSuccess, milestone, hasOtherStakeholderMilestones, onSelect }) {
  const { id: milestoneId, type, description, sent_for_review_user, reviewer_name, statusName } = milestone;
  const { triggerError } = useContext(ErrorContext);
  const classes = useStyles();
  const params = useParams();

  const [isYesButtonSelected, setIsYesButtonSelected] = useState((milestone.id !== -1 && milestone.sent_for_review_user === null));
  const [isNoButtonSelected, setIsNoButtonSelected] = useState(sent_for_review_user === null && statusName === "Completed");
  const [isResubmittedButtonSelected, setIsResubmittedButtonSelected] = useState(statusName === "Resubmitted");

  useEffect(() => {
    setIsYesButtonSelected(isYesButtonSelected);
    setIsNoButtonSelected(isNoButtonSelected);
    setIsResubmittedButtonSelected(isResubmittedButtonSelected);
  });

  const handleYesChoiceSelection = (e) => {
    setIsYesButtonSelected(!isYesButtonSelected);
    setIsNoButtonSelected(false);
    setIsResubmittedButtonSelected(false);
    onSelect([
      {
        index: index,
        milestoneType: type || milestone.milestoneType,
        action: "yes",
        value: !isYesButtonSelected ? "yes" : "",
      }
    ]);
  }

  const handleNoChoiceSelection = (e) => {
    setIsYesButtonSelected(false);
    setIsNoButtonSelected(!isNoButtonSelected);
    setIsResubmittedButtonSelected(false);
    onSelect([
      {
        index: index,
        milestoneType: type || milestone.milestoneType,
        action: "no",
        value: !isNoButtonSelected ? "no" : "",
      }
    ]);

  }

  const handleResubmitChoiceSelection = (e) => {
    setIsYesButtonSelected(false);
    setIsNoButtonSelected(false);
    setIsResubmittedButtonSelected(!isResubmittedButtonSelected);
    onSelect([
      {
        index: index,
        milestoneType: type || milestone.milestoneType,
        action: "resubmit",
        value: !isResubmittedButtonSelected ? "resubmit" : "",
      }
    ]);

  }

  const rejectMilestone = () => {
    apiFetch('/initiatives/' + params.id + '/milestones/' + milestoneId + '/reject',
      'PATCH', JSON.stringify({ id: milestoneId }),
      onSuccess,
      () => triggerError({
        title: 'Send For Approval Error',
        description: `We were unable to send this Milestone for revision. Please try again.`,
        onRetry: rejectMilestone
      }));
    setIsYesButtonSelected((milestone.id !== -1 && milestone.sent_for_review_user === null));
    setIsNoButtonSelected(hasOtherStakeholderMilestones);
    setIsResubmittedButtonSelected(false);
  };

  const approveMilestone = () => {
    apiFetch('/initiatives/' + params.id + '/milestones/' + milestoneId + '/approve',
      'PATCH', JSON.stringify({ id: milestoneId }),
      onSuccess,
      () => triggerError({
        title: 'Send For Approval Error',
        description: `We were unable to send this Milestone for approval. Please try again.`,
        onRetry: approveMilestone
      }));
    setIsYesButtonSelected((milestone.id !== -1 && milestone.sent_for_review_user === null));
    setIsNoButtonSelected(hasOtherStakeholderMilestones);
    setIsResubmittedButtonSelected(false);
  };

  const getMandatoryMilestonesBtns = (initiativeTeam) => {
    const reviewers = milestoneReviewers.filter(item => item.mandatory_type === type);
    if (reviewer_name) {
      return (
        <div className={classes.statusDiv}>
          Initiative reviewed by {reviewer_name}
        </div>
      )
    }

    if (!canEditInitiative(initiativeTeam) && !loggedInUserInList(reviewers)) {
      if (sent_for_review_user) {
        return (<div className={classes.approvalButton}>
          Review Requested
        </div>);
      }
      return '';
    }

    if (sent_for_review_user) {
      if (loggedInUserInList(reviewers)) {
        return (
          <div className={classes.approvalContainer}>
            <Button variant="contained" color="primary"
              className={classes.mandatoryApproverStyle} onClick={approveMilestone}>
              REVIEWED & APPROVED
            </Button>
            <Button variant="contained" color="primary"
              className={classes.mandatoryApproverStyle} onClick={rejectMilestone}>
              NEEDS REVISION
            </Button>
          </div>
        );
      }
      return (
        <div className={classes.approvalButton}>
          Review Requested
        </div>
      );
    }
  };


  const ApprovalButtons = (initiativeTeam) => {
    return (
      <div>
        <div className={classes.container}>
          <div style={{ gridColumnEnd: 'span 10' }}>
            <div>{getMandatoryMilestonesBtns(initiativeTeam, classes)}</div>
          </div>
          <div style={{ gridColumnEnd: 'span 2' }}>
            {getRadioButtons(initiativeTeam, classes)}
          </div>
        </div>
      </div>
    );
  }

  const getRadioButtons = (initiativeTeam) => {
    const reviewers = milestoneReviewers.filter(item => item.mandatory_type === type);

    if (reviewer_name) {
      return (
        <div className={classes.gridContainer}>
          <div className={classes.gridItem}>Yes</div>
          <div className={classes.gridItem}>No</div>
          <div className={classes.gridItem}>Resubmit</div>
          <div className={classes.gridItem}>
            {
              milestone.review_verdict ? <CheckCircle className={classes.checkedCircle} /> : <HighlightOff className={classes.highlightOff} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              isNoButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} onClick={handleNoChoiceSelection} /> :
                <RadioButtonUncheckedIcon className={classes.unSelected} onClick={handleNoChoiceSelection} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              milestone.review_verdict ? null :
                isResubmittedButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} onClick={handleResubmitChoiceSelection} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} onClick={handleResubmitChoiceSelection} />
            }
          </div>
        </div>
      )
    }

    if (!canEditInitiative(initiativeTeam) && !loggedInUserInList(reviewers)) {
      if (sent_for_review_user) {
        return (
          <div className={classes.gridContainer}>
            <div className={classes.gridItem}>Yes</div>
            <div className={classes.gridItem}>No</div>
            <div className={classes.gridItem}>Resubmit</div>
            <div className={classes.gridItem}>
              {
                statusName === 'Resubmitted' ? <HighlightOff className={classes.highlightOff} /> : statusName === "Not Started" && sent_for_review_user !== null ?
                  <PendingIcon className={classes.pendingIcon} /> :
                  isYesButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                    <RadioButtonUncheckedIcon className={classes.unSelected} />

              }
            </div>
            <div className={classes.gridItem}>
              {
                isNoButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} />
              }
            </div>
            <div className={classes.gridItem}>
              {
                statusName === 'Resubmitted' ? <PendingIcon className={classes.pendingIcon} /> : ''
              }
            </div>
          </div>
        )
      }
      return (
        <div className={classes.gridContainer}>
          <div className={classes.gridItem}>Yes</div>
          <div className={classes.gridItem}>No</div>
          <div className={classes.gridItem}>Resubmit</div>
          <div className={classes.gridItem}>
            {
              isYesButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                <RadioButtonUncheckedIcon className={classes.unSelected} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              milestone.statusName === "Completed" ? <CheckCircle className={classes.checkedCircle} /> :
                isNoButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} />
            }
          </div>
          <div className={classes.gridItem}>
          </div>
        </div>
      );
    }

    if (sent_for_review_user) {
      if (loggedInUserInList(reviewers)) {
        return (
          <div className={classes.gridContainer}>
            <div className={classes.gridItem}>Yes</div>
            <div className={classes.gridItem}>No</div>
            <div className={classes.gridItem}>Resubmit</div>
            <div className={classes.gridItem}>
              {
                statusName === 'Resubmitted' ? <HighlightOff className={classes.highlightOff} /> : <PendingIcon className={classes.pendingIcon} />
              }
            </div>
            <div className={classes.gridItem}>
            </div>
            <div className={classes.gridItem}>
              {
                statusName === 'Resubmitted' ? <PendingIcon className={classes.pendingIcon} /> : ''
              }
            </div>
          </div>
        );
      }
      return (
        <div className={classes.gridContainer}>
          <div className={classes.gridItem}>Yes</div>
          <div className={classes.gridItem}>No</div>
          <div className={classes.gridItem}>Resubmit</div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <HighlightOff className={classes.highlightOff} /> : <PendingIcon className={classes.pendingIcon} />
            }
          </div>
          <div className={classes.gridItem}>
          </div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <PendingIcon className={classes.pendingIcon} /> : ''
            }
          </div>
        </div>
      );
    }

    return (
      canEditInitiative(initiativeTeam) ?
        <div className={classes.gridContainer}>
          <div className={classes.gridItem}>Yes</div>
          <div className={classes.gridItem}>No</div>
          <div className={classes.gridItem}>Resubmit</div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <HighlightOff className={classes.highlightOff} /> :
                isYesButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} onClick={handleYesChoiceSelection} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} onClick={handleYesChoiceSelection} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              (hasOtherStakeholderMilestones || (sent_for_review_user === null && statusName === "Completed")) ? <CheckCircle className={classes.checkedCircle} /> :
                isNoButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} onClick={handleNoChoiceSelection} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} onClick={handleNoChoiceSelection} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <PendingIcon className={classes.pendingIcon} /> : ''
            }
          </div>
        </div> : <div className={classes.gridContainer}>
          <div className={classes.gridItem}>Yes</div>
          <div className={classes.gridItem}>No</div>
          <div className={classes.gridItem}>Resubmit</div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <HighlightOff className={classes.highlightOff} /> :
                isYesButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              (hasOtherStakeholderMilestones || (sent_for_review_user === null && statusName === "Completed")) ? <CheckCircle className={classes.checkedCircle} /> :
                isNoButtonSelected ? <RadioButtonCheckedIcon className={classes.unSelected} /> :
                  <RadioButtonUncheckedIcon className={classes.unSelected} />
            }
          </div>
          <div className={classes.gridItem}>
            {
              statusName === 'Resubmitted' ? <PendingIcon className={classes.pendingIcon} /> : ''
            }
          </div>
        </div>
    );
  }

  return (
    <div className={classes.cardRoot}>
      <Typography>
        {description}
      </Typography>
      <br />
      {ApprovalButtons(initiativeTeam, classes)}
    </div>

  );
};
